import { useState, useMemo, useRef } from "react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useRefresh,
  type Identifier,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { useCrmIdentity } from "@/hooks/useCrmIdentity";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { CrmDataProvider } from "../providers/types";
import type {
  Company,
  Contact,
  ReassignPortfolioResult,
  Sale,
  Tag,
} from "../types";

interface PortfolioReassignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ReassignMode = "full" | "company" | "contact" | "tag";

// eslint-disable-next-line react-refresh/only-export-components
export function computeReassignmentFingerprint(params: {
  fromSalesId: string;
  toSalesId: string;
  mode: ReassignMode;
  companyIds: number[];
  contactIds: number[];
  tagId: string;
}): string {
  const sortedCompanyIds = [...params.companyIds]
    .sort((a, b) => a - b)
    .join(",");
  const sortedContactIds = [...params.contactIds]
    .sort((a, b) => a - b)
    .join(",");
  return JSON.stringify({
    from: params.fromSalesId,
    to: params.toSalesId,
    mode: params.mode,
    companyIds: sortedCompanyIds,
    contactIds: sortedContactIds,
    tagId: params.tagId,
  });
}

const getContactEmail = (c: any): string => {
  if (typeof c?.email === "string" && c.email) return c.email;
  if (Array.isArray(c?.email_jsonb) && c.email_jsonb[0]?.email) {
    return c.email_jsonb[0].email;
  }
  return "";
};

export function PortfolioReassignmentDialog({
  open,
  onOpenChange,
}: PortfolioReassignmentDialogProps) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  const { identity } = useCrmIdentity();

  const callerRole =
    identity?.role || (identity?.administrator ? "admin" : "sales");
  const isSuperadmin = callerRole === "superadmin";
  const isAdmin = callerRole === "admin" || isSuperadmin;

  const [fromSalesId, setFromSalesId] = useState<string>("");
  const [toSalesId, setToSalesId] = useState<string>("");
  const [mode, setMode] = useState<ReassignMode>("full");
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [selectedTagId, setSelectedTagId] = useState<string>("");

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 25;

  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isSelectingAllMatching, setIsSelectingAllMatching] = useState(false);
  const [previewResult, setPreviewResult] =
    useState<ReassignPortfolioResult | null>(null);
  const [previewFingerprint, setPreviewFingerprint] = useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const bulkSelectVersionRef = useRef(0);
  const previewSequenceRef = useRef(0);

  const isBusy = isPreviewLoading || isExecuting || isSelectingAllMatching;

  const currentFingerprint = useMemo(() => {
    return computeReassignmentFingerprint({
      fromSalesId,
      toSalesId,
      mode,
      companyIds: selectedCompanyIds,
      contactIds: selectedContactIds,
      tagId: selectedTagId,
    });
  }, [
    fromSalesId,
    toSalesId,
    mode,
    selectedCompanyIds,
    selectedContactIds,
    selectedTagId,
  ]);

  // Fetch sales list
  const { data: salesList = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  // Role-based source options:
  // Superadmin can reassign from any profile (active or disabled).
  // Standard admin can reassign only from sales profiles (active or disabled).
  const sourceOptions = useMemo(() => {
    return salesList.filter(
      (s) => isSuperadmin || (s.role || "sales") === "sales",
    );
  }, [salesList, isSuperadmin]);

  // Role-based target options:
  // Target MUST be active (!disabled) and distinct from source.
  // Superadmin can assign to any active profile.
  // Standard admin can assign only to active sales profiles.
  const targetOptions = useMemo(() => {
    return salesList.filter((s) => {
      if (s.disabled) return false;
      if (String(s.id) === fromSalesId) return false;
      if (!isSuperadmin && (s.role || "sales") !== "sales") return false;
      return true;
    });
  }, [salesList, fromSalesId, isSuperadmin]);

  // Fetch companies for source salesperson if mode === 'company'
  const {
    data: companies = [],
    total: totalCompanies = 0,
    isLoading: isCompaniesLoading,
  } = useGetList<Company>(
    "companies_summary",
    {
      filter: fromSalesId
        ? {
            sales_id: fromSalesId,
            ...(searchQuery.trim() ? { q: searchQuery.trim() } : {}),
          }
        : {},
      pagination: { page, perPage },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: mode === "company" && !!fromSalesId },
  );

  // Fetch contacts for source salesperson if mode === 'contact'
  const {
    data: contacts = [],
    total: totalContacts = 0,
    isLoading: isContactsLoading,
  } = useGetList<Contact>(
    "contacts_summary",
    {
      filter: fromSalesId
        ? {
            sales_id: fromSalesId,
            ...(searchQuery.trim() ? { q: searchQuery.trim() } : {}),
          }
        : {},
      pagination: { page, perPage },
      sort: { field: "first_name", order: "ASC" },
    },
    { enabled: mode === "contact" && !!fromSalesId },
  );

  // Fetch tags if mode === 'tag'
  const { data: tags = [] } = useGetList<Tag>(
    "tags",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: mode === "tag" },
  );

  // Fallback client-side filtering (handles mocks or providers that do not server-filter by q)
  const filteredCompanies = useMemo(() => {
    if (!searchQuery.trim()) return companies;
    const q = searchQuery.toLowerCase().trim();
    return companies.filter((c) => c.name?.toLowerCase().includes(q));
  }, [companies, searchQuery]);

  const filteredContacts = useMemo(() => {
    if (!searchQuery.trim()) return contacts;
    const q = searchQuery.toLowerCase().trim();
    return contacts.filter((c: any) => {
      const email = getContactEmail(c).toLowerCase();
      const firstName = (c.first_name || "").toLowerCase();
      const lastName = (c.last_name || "").toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();
      const companyName = (c.company_name || "").toLowerCase();
      return (
        firstName.includes(q) ||
        lastName.includes(q) ||
        fullName.includes(q) ||
        email.includes(q) ||
        companyName.includes(q)
      );
    });
  }, [contacts, searchQuery]);

  const totalMatching =
    mode === "company"
      ? totalCompanies || filteredCompanies.length
      : mode === "contact"
        ? totalContacts || filteredContacts.length
        : 0;

  const totalPages = Math.max(1, Math.ceil(totalMatching / perPage));
  const isLoadingItems =
    mode === "company"
      ? isCompaniesLoading
      : mode === "contact"
        ? isContactsLoading
        : false;

  const fromSales = salesList.find((s) => String(s.id) === fromSalesId);
  const toSales = salesList.find((s) => String(s.id) === toSalesId);

  // State invalidation: any change to source, target, mode, search, or selection resets preview
  const handleReset = () => {
    previewSequenceRef.current++;
    setPreviewResult(null);
    setPreviewFingerprint(null);
    setErrorMessage(null);
  };

  const handleFromChange = (val: string) => {
    bulkSelectVersionRef.current++;
    setFromSalesId(val);
    setSelectedCompanyIds([]);
    setSelectedContactIds([]);
    setSearchQuery("");
    setPage(1);
    handleReset();
  };

  const handleToChange = (val: string) => {
    bulkSelectVersionRef.current++;
    setToSalesId(val);
    handleReset();
  };

  const handleModeChange = (val: ReassignMode) => {
    bulkSelectVersionRef.current++;
    setMode(val);
    setSelectedCompanyIds([]);
    setSelectedContactIds([]);
    setSelectedTagId("");
    setSearchQuery("");
    setPage(1);
    handleReset();
  };

  const handleSearchChange = (val: string) => {
    bulkSelectVersionRef.current++;
    setSearchQuery(val);
    setPage(1);
    handleReset();
  };

  // Bulk selection: Select all currently visible results
  const handleSelectVisible = () => {
    if (mode === "company") {
      const ids = filteredCompanies.map((c) => c.id as number);
      setSelectedCompanyIds((prev) => Array.from(new Set([...prev, ...ids])));
    } else if (mode === "contact") {
      const ids = filteredContacts.map((c) => c.id as number);
      setSelectedContactIds((prev) => Array.from(new Set([...prev, ...ids])));
    }
    handleReset();
  };

  // Bulk selection: Select all matching results across all pages with genuine pagination
  const handleSelectAllMatching = async () => {
    if (!fromSalesId || (mode !== "company" && mode !== "contact")) return;

    const requestVersion = ++bulkSelectVersionRef.current;
    const currentSource = fromSalesId;
    const currentMode = mode;
    const currentSearch = searchQuery.trim();

    setIsSelectingAllMatching(true);
    setErrorMessage(null);

    try {
      const resource =
        mode === "company" ? "companies_summary" : "contacts_summary";
      const filter: Record<string, any> = {
        sales_id: fromSalesId,
        ...(currentSearch ? { q: currentSearch } : {}),
      };

      const PAGE_SIZE = 500;
      let currentPage = 1;
      const collectedIds = new Set<number>();
      let expectedTotal: number | null = null;

      while (true) {
        const res = await dataProvider.getList<any>(resource, {
          filter,
          pagination: { page: currentPage, perPage: PAGE_SIZE },
          sort: {
            field: "id",
            order: "ASC",
          },
        });

        // Concurrency safety: discard stale results if request context changed
        if (
          bulkSelectVersionRef.current !== requestVersion ||
          fromSalesId !== currentSource ||
          mode !== currentMode ||
          searchQuery.trim() !== currentSearch
        ) {
          return;
        }

        if (expectedTotal === null) {
          if (typeof res.total === "number") {
            expectedTotal = res.total;
          } else {
            throw new Error(
              "Il server non ha restituito il conteggio totale dei record",
            );
          }
        } else if (
          typeof res.total === "number" &&
          res.total !== expectedTotal
        ) {
          throw new Error(
            `Il totale dei record è cambiato durante il recupero (iniziale: ${expectedTotal}, attuale: ${res.total})`,
          );
        }

        const items = res.data || [];
        for (const item of items) {
          if (item?.id != null) {
            collectedIds.add(Number(item.id));
          }
        }

        if (
          expectedTotal === 0 ||
          collectedIds.size >= expectedTotal ||
          items.length === 0
        ) {
          break;
        }

        if (items.length < PAGE_SIZE) {
          break;
        }

        currentPage++;
      }

      // Concurrency safety: final check before applying selection
      if (
        bulkSelectVersionRef.current !== requestVersion ||
        fromSalesId !== currentSource ||
        mode !== currentMode ||
        searchQuery.trim() !== currentSearch
      ) {
        return;
      }

      // Consistency check: fail closed if unique collected count does not match expected total
      if (expectedTotal !== null && collectedIds.size !== expectedTotal) {
        throw new Error(
          `Incoerenza nel conteggio dei record: attesi ${expectedTotal}, recuperati ${collectedIds.size}`,
        );
      }

      const allIds = Array.from(collectedIds);
      if (mode === "company") {
        setSelectedCompanyIds((prev) =>
          Array.from(new Set([...prev, ...allIds])),
        );
      } else if (mode === "contact") {
        setSelectedContactIds((prev) =>
          Array.from(new Set([...prev, ...allIds])),
        );
      }
      handleReset();
    } catch (err: any) {
      setErrorMessage(
        err?.message || "Errore durante il recupero dei record corrispondenti",
      );
    } finally {
      setIsSelectingAllMatching(false);
    }
  };

  // Bulk selection: Clear selection
  const handleClearSelection = () => {
    if (mode === "company") {
      setSelectedCompanyIds([]);
    } else if (mode === "contact") {
      setSelectedContactIds([]);
    }
    handleReset();
  };

  const canRunPreview =
    isAdmin &&
    Boolean(fromSalesId) &&
    Boolean(toSalesId) &&
    fromSalesId !== toSalesId &&
    (mode === "full" ||
      (mode === "company" && selectedCompanyIds.length > 0) ||
      (mode === "contact" && selectedContactIds.length > 0) ||
      (mode === "tag" && Boolean(selectedTagId)));

  const isPreviewSynchronized =
    previewResult !== null &&
    previewFingerprint !== null &&
    currentFingerprint === previewFingerprint;

  const canExecute = canRunPreview && isPreviewSynchronized && !isBusy;

  const handlePreview = async () => {
    if (!canRunPreview) return;
    const requestFingerprint = currentFingerprint;
    const requestSeq = ++previewSequenceRef.current;
    setIsPreviewLoading(true);
    setErrorMessage(null);

    try {
      const res = await dataProvider.reassignPortfolio({
        from_sales_id: fromSalesId as Identifier,
        to_sales_id: toSalesId as Identifier,
        mode,
        company_ids: mode === "company" ? selectedCompanyIds : undefined,
        contact_ids: mode === "contact" ? selectedContactIds : undefined,
        tag_id: mode === "tag" ? (selectedTagId as Identifier) : undefined,
        dry_run: true,
      });

      if (previewSequenceRef.current !== requestSeq) {
        return;
      }

      setPreviewResult(res);
      setPreviewFingerprint(requestFingerprint);
    } catch (err: any) {
      if (previewSequenceRef.current !== requestSeq) {
        return;
      }
      setErrorMessage(
        err?.message || "Errore durante il calcolo dell'anteprima",
      );
      setPreviewResult(null);
      setPreviewFingerprint(null);
    } finally {
      if (previewSequenceRef.current === requestSeq) {
        setIsPreviewLoading(false);
      }
    }
  };

  const handleExecute = async () => {
    if (!canExecute || !previewResult) return;
    setIsExecuting(true);
    setErrorMessage(null);

    try {
      await dataProvider.reassignPortfolio({
        from_sales_id: fromSalesId as Identifier,
        to_sales_id: toSalesId as Identifier,
        mode,
        company_ids: mode === "company" ? selectedCompanyIds : undefined,
        contact_ids: mode === "contact" ? selectedContactIds : undefined,
        tag_id: mode === "tag" ? (selectedTagId as Identifier) : undefined,
        dry_run: false,
      });

      notify("Riassegnazione portafoglio completata con successo", {
        type: "info",
      });
      queryClient.invalidateQueries();
      refresh();
      onOpenChange(false);
    } catch (err: any) {
      setErrorMessage(
        err?.message || "Errore durante l'esecuzione della riassegnazione",
      );
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Riassegnazione Portafoglio Commerciale</DialogTitle>
          <DialogDescription>
            Trasferisci aziende, contatti e opportunità da un commerciale a un
            altro garantendo la coerenza dei dati.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!isAdmin && (
            <Alert variant="destructive">
              <AlertTitle>Accesso Negato</AlertTitle>
              <AlertDescription>
                Solo amministratori e superamministratori possono effettuare la
                riassegnazione del portafoglio.
              </AlertDescription>
            </Alert>
          )}

          {errorMessage && (
            <Alert variant="destructive">
              <AlertTitle>Attenzione</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {/* Source & Destination selectors */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Commerciale di Origine (Source)</Label>
              <Select
                value={fromSalesId}
                onValueChange={handleFromChange}
                disabled={isBusy}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona commerciale..." />
                </SelectTrigger>
                <SelectContent>
                  {sourceOptions.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.first_name} {s.last_name} ({s.role || "sales"})
                      {s.disabled ? " [Disabilitato]" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Commerciale di Destinazione (Target)</Label>
              <Select
                value={toSalesId}
                onValueChange={handleToChange}
                disabled={isBusy}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona destinazione..." />
                </SelectTrigger>
                <SelectContent>
                  {targetOptions.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.first_name} {s.last_name} ({s.role || "sales"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Flow indication banner */}
          {fromSales && toSales && (
            <div
              data-testid="reassignment-flow-banner"
              className="flex items-center justify-between p-2.5 rounded-md border bg-muted/40 text-xs font-medium"
            >
              <div className="flex items-center gap-1.5 truncate">
                <span className="text-muted-foreground">Origine:</span>
                <span className="font-semibold text-foreground">
                  {fromSales.first_name} {fromSales.last_name} (
                  {fromSales.role || "sales"})
                  {fromSales.disabled ? " [Disabilitato]" : ""}
                </span>
              </div>
              <span className="text-muted-foreground px-2">➔</span>
              <div className="flex items-center gap-1.5 truncate">
                <span className="text-muted-foreground">Destinazione:</span>
                <span className="font-semibold text-foreground">
                  {toSales.first_name} {toSales.last_name} (
                  {toSales.role || "sales"})
                </span>
              </div>
            </div>
          )}

          {/* Mode Selector */}
          <div className="space-y-2">
            <Label>Ambito di Riassegnazione</Label>
            <Select
              value={mode}
              onValueChange={handleModeChange}
              disabled={isBusy}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full">
                  Intero Portafoglio (Tutte le aziende, contatti, trattative)
                </SelectItem>
                <SelectItem value="company">Per Aziende Selezionate</SelectItem>
                <SelectItem value="contact">
                  Per Contatti Selezionati (auto-espande all&apos;azienda)
                </SelectItem>
                <SelectItem value="tag">Per Tag Contatti</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Mode: Company */}
          {mode === "company" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Seleziona Aziende</Label>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>
                    Corrispondenti:{" "}
                    <strong
                      data-testid="total-matching-count"
                      className="text-foreground"
                    >
                      {totalMatching}
                    </strong>
                  </span>
                  <span>
                    Selezionate:{" "}
                    <strong
                      data-testid="selected-count"
                      className="text-primary font-bold"
                    >
                      {selectedCompanyIds.length}
                    </strong>
                  </span>
                  {searchQuery.trim() && (
                    <span
                      data-testid="active-filter-badge"
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-primary/10 text-primary"
                    >
                      Filtro attivo: &quot;{searchQuery.trim()}&quot;
                    </span>
                  )}
                </div>
              </div>

              {/* Search bar */}
              <div className="flex items-center gap-2">
                <Input
                  id="reassign-company-search"
                  placeholder="Cerca aziende per nome..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  disabled={isBusy}
                  className="h-8 text-xs"
                />
                {searchQuery && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-xs"
                    disabled={isBusy}
                    onClick={() => handleSearchChange("")}
                  >
                    Resetta
                  </Button>
                )}
              </div>

              {/* Bulk selection controls */}
              <div className="flex flex-wrap items-center justify-between gap-1.5 pt-1">
                <div className="flex items-center gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleSelectVisible}
                    disabled={filteredCompanies.length === 0 || isBusy}
                  >
                    Seleziona visibili
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleSelectAllMatching}
                    disabled={totalMatching === 0 || isBusy}
                  >
                    {isSelectingAllMatching
                      ? "Caricamento..."
                      : `Seleziona tutti i corrispondenti (${totalMatching})`}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground hover:text-foreground"
                  onClick={handleClearSelection}
                  disabled={selectedCompanyIds.length === 0 || isBusy}
                >
                  Deseleziona tutti
                </Button>
              </div>

              {/* List container */}
              <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                {isLoadingItems ? (
                  <p className="text-sm text-muted-foreground p-2">
                    Caricamento aziende in corso...
                  </p>
                ) : filteredCompanies.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-2">
                    {searchQuery
                      ? "Nessuna azienda trovata per la ricerca specificata."
                      : "Nessuna azienda associata a questo commerciale."}
                  </p>
                ) : (
                  filteredCompanies.map((c) => {
                    const isChecked = selectedCompanyIds.includes(
                      c.id as number,
                    );
                    return (
                      <label
                        key={c.id}
                        className="flex items-center space-x-2 text-sm p-1.5 hover:bg-muted rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isBusy}
                          onChange={(e) => {
                            handleReset();
                            if (e.target.checked) {
                              setSelectedCompanyIds((prev) => [
                                ...prev,
                                c.id as number,
                              ]);
                            } else {
                              setSelectedCompanyIds((prev) =>
                                prev.filter((id) => id !== c.id),
                              );
                            }
                          }}
                        />
                        <span className="truncate">{c.name}</span>
                      </label>
                    );
                  })
                )}
              </div>

              {/* Pagination controls */}
              <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                <span>
                  Pagina {page} di {totalPages} ({totalMatching} totali)
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    disabled={page <= 1 || isLoadingItems || isBusy}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Precedente
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    disabled={page >= totalPages || isLoadingItems || isBusy}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Successiva
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Mode: Contact */}
          {mode === "contact" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Seleziona Contatti</Label>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>
                    Corrispondenti:{" "}
                    <strong
                      data-testid="total-matching-count"
                      className="text-foreground"
                    >
                      {totalMatching}
                    </strong>
                  </span>
                  <span>
                    Selezionati:{" "}
                    <strong
                      data-testid="selected-count"
                      className="text-primary font-bold"
                    >
                      {selectedContactIds.length}
                    </strong>
                  </span>
                  {searchQuery.trim() && (
                    <span
                      data-testid="active-filter-badge"
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-primary/10 text-primary"
                    >
                      Filtro attivo: &quot;{searchQuery.trim()}&quot;
                    </span>
                  )}
                </div>
              </div>

              {/* Search bar */}
              <div className="flex items-center gap-2">
                <Input
                  id="reassign-contact-search"
                  placeholder="Cerca contatti per nome, cognome, email o azienda..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  disabled={isBusy}
                  className="h-8 text-xs"
                />
                {searchQuery && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-xs"
                    disabled={isBusy}
                    onClick={() => handleSearchChange("")}
                  >
                    Resetta
                  </Button>
                )}
              </div>

              {/* Bulk selection controls */}
              <div className="flex flex-wrap items-center justify-between gap-1.5 pt-1">
                <div className="flex items-center gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleSelectVisible}
                    disabled={filteredContacts.length === 0 || isBusy}
                  >
                    Seleziona visibili
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleSelectAllMatching}
                    disabled={totalMatching === 0 || isBusy}
                  >
                    {isSelectingAllMatching
                      ? "Caricamento..."
                      : `Seleziona tutti i corrispondenti (${totalMatching})`}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground hover:text-foreground"
                  onClick={handleClearSelection}
                  disabled={selectedContactIds.length === 0 || isBusy}
                >
                  Deseleziona tutti
                </Button>
              </div>

              {/* List container */}
              <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                {isLoadingItems ? (
                  <p className="text-sm text-muted-foreground p-2">
                    Caricamento contatti in corso...
                  </p>
                ) : filteredContacts.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-2">
                    {searchQuery
                      ? "Nessun contatto trovato per la ricerca specificata."
                      : "Nessun contatto associato a questo commerciale."}
                  </p>
                ) : (
                  filteredContacts.map((c: any) => {
                    const isChecked = selectedContactIds.includes(
                      c.id as number,
                    );
                    const email = getContactEmail(c);
                    return (
                      <label
                        key={c.id}
                        className="flex items-center space-x-2 text-sm p-1.5 hover:bg-muted rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isBusy}
                          onChange={(e) => {
                            handleReset();
                            if (e.target.checked) {
                              setSelectedContactIds((prev) => [
                                ...prev,
                                c.id as number,
                              ]);
                            } else {
                              setSelectedContactIds((prev) =>
                                prev.filter((id) => id !== c.id),
                              );
                            }
                          }}
                        />
                        <div className="flex flex-col truncate">
                          <span className="font-medium">
                            {c.first_name} {c.last_name}
                            {c.company_name ? (
                              <span className="text-muted-foreground ml-1.5 font-normal">
                                ({c.company_name})
                              </span>
                            ) : null}
                          </span>
                          {email ? (
                            <span className="text-xs text-muted-foreground">
                              {email}
                            </span>
                          ) : null}
                        </div>
                      </label>
                    );
                  })
                )}
              </div>

              {/* Pagination controls */}
              <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                <span>
                  Pagina {page} di {totalPages} ({totalMatching} totali)
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    disabled={page <= 1 || isLoadingItems || isBusy}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Precedente
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    disabled={page >= totalPages || isLoadingItems || isBusy}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Successiva
                  </Button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground/80 italic">
                Nota: I contatti associati ad un&apos;azienda espandono
                automaticamente la riassegnazione all&apos;intera azienda e a
                tutti i relativi contatti e trattative collegate.
              </p>
            </div>
          )}

          {/* Mode: Tag */}
          {mode === "tag" && (
            <div className="space-y-2">
              <Label>Tag Contatto</Label>
              <Select
                value={selectedTagId}
                disabled={isBusy}
                onValueChange={(val) => {
                  setSelectedTagId(val);
                  handleReset();
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona tag..." />
                </SelectTrigger>
                <SelectContent>
                  {tags.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Preview summary */}
          {previewResult && (
            <div className="mt-4 p-4 border rounded-md bg-muted/40 space-y-3">
              <h4 className="font-semibold text-sm">
                Riepilogo Anteprima Riassegnazione:
              </h4>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-background p-2 rounded border">
                  <span className="text-muted-foreground block">Aziende</span>
                  <span className="font-bold text-base">
                    {previewResult.companies_count}
                  </span>
                </div>
                <div className="bg-background p-2 rounded border">
                  <span className="text-muted-foreground block">Contatti</span>
                  <span className="font-bold text-base">
                    {previewResult.contacts_count}
                  </span>
                </div>
                <div className="bg-background p-2 rounded border">
                  <span className="text-muted-foreground block">
                    Trattative (Deals)
                  </span>
                  <span className="font-bold text-base">
                    {previewResult.deals_count}
                  </span>
                </div>
                <div className="bg-background p-2 rounded border">
                  <span className="text-muted-foreground block">
                    Attività (Tasks)
                  </span>
                  <span className="font-bold text-base">
                    {previewResult.tasks_count}
                  </span>
                </div>
                <div className="bg-background p-2 rounded border">
                  <span className="text-muted-foreground block">
                    Note Contatti
                  </span>
                  <span className="font-bold text-base">
                    {previewResult.contact_notes_count}
                  </span>
                </div>
                <div className="bg-background p-2 rounded border">
                  <span className="text-muted-foreground block">
                    Note Trattative
                  </span>
                  <span className="font-bold text-base">
                    {previewResult.deal_notes_count}
                  </span>
                </div>
              </div>

              {previewResult.expanded_to_companies && (
                <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200">
                  <AlertTitle className="font-semibold">
                    Espansione Portafoglio Aziendale Rilevata
                  </AlertTitle>
                  <AlertDescription className="text-xs">
                    Uno o più contatti selezionati appartengono ad
                    un&apos;azienda. Per prevenire split-ownership e mantenere
                    l&apos;integrità commerciale, l&apos;azienda e tutte le
                    relative trattative e contatti collegati verranno
                    riassegnati al nuovo commerciale.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between items-center gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExecuting || isSelectingAllMatching}
          >
            Annulla
          </Button>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={!canRunPreview || isBusy}
              onClick={handlePreview}
            >
              {isPreviewLoading ? "Calcolo in corso..." : "Calcola Anteprima"}
            </Button>

            <Button
              type="button"
              variant="default"
              disabled={!canExecute}
              onClick={handleExecute}
            >
              {isExecuting ? "Riassegnazione..." : "Conferma Riassegnazione"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
